import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  ogImageAlt?: string;
  publishedTime?: string;
  modifiedTime?: string;
  schema?: Record<string, unknown>;
}

export default function SEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  ogImageAlt,
  publishedTime,
  modifiedTime,
  schema,
}: SEOProps) {
  const fullTitle = title.includes('韩漫之家') ? title : `${title} - 韩漫之家`;

  useEffect(() => {
    document.title = fullTitle;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.name = name;
        document.head.appendChild(el);
      }
      el.content = content;
    };

    const setProperty = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    const setNameMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.name = name;
        document.head.appendChild(el);
      }
      el.content = content;
    };

    setMeta('description', description);
    setMeta('keywords', keywords);
    setProperty('og:title', fullTitle);
    setProperty('og:description', description);
    setProperty('og:type', ogType);
    setProperty('og:url', canonicalUrl);
    setProperty('og:site_name', '韩漫之家');
    setProperty('og:locale', 'zh_CN');
    setProperty('twitter:card', 'summary_large_image');
    setProperty('twitter:title', fullTitle);
    setProperty('twitter:description', description);

    if (ogImage) {
      setProperty('og:image', ogImage);
      setProperty('twitter:image', ogImage);
      if (ogImageAlt) {
        setProperty('og:image:alt', ogImageAlt);
      }
    }

    const existingCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (existingCanonical) {
      existingCanonical.href = canonicalUrl;
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = canonicalUrl;
      document.head.appendChild(link);
    }

    if (publishedTime) {
      setNameMeta('article:published_time', publishedTime);
    }
    if (modifiedTime) {
      setNameMeta('article:modified_time', modifiedTime);
    }
    setMeta('last-modified', new Date().toISOString().split('T')[0]);

    // Schema.org JSON-LD
    const existingSchema = document.querySelector('script[type="application/ld+json"][data-seo]');
    if (existingSchema) existingSchema.remove();

    if (schema) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo', 'true');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }
  }, [fullTitle, description, keywords, canonicalUrl, ogType, ogImage, ogImageAlt, publishedTime, modifiedTime, schema]);

  return null;
}